import React from 'react';
import PropTypes from 'prop-types';
import { Check, X } from 'lucide-react';
import styles from './FilterChips.module.scss';

/* ────────────────────────────────────────────────────────────
 * FilterChips — chip-style multi/single select for filter rows.
 *
 * Replaces stacked <Select> filter columns on list screens with
 * > 2 active filters. Pairs with FilterChips.Clear at the end
 * of a chip row, and exposes a `multi` mode for non-exclusive
 * filters.
 *
 *   <FilterChips
 *     items={[
 *       { key: 'all',    label: 'All' },
 *       { key: 'low',    label: 'Low stock', count: 3 },
 *       { key: 'out',    label: 'Out of stock' },
 *     ]}
 *     value={selected}
 *     onChange={setSelected}
 *     multi={false}
 *   />
 *
 * Chip motion (MOTION.md §5): hover background-color fade,
 * :active scale(0.98), focus-visible ring.
 * ──────────────────────────────────────────────────────────── */

const Chip = ({ active, disabled, onClick, children, count }) => {
  const cls = [
    styles.chip,
    active ? styles.chipActive : '',
    disabled ? styles.chipDisabled : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={active}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      className={cls}
      onClick={onClick}
    >
      {active && (
        <span className={styles.tick} aria-hidden="true">
          <Check size={12} strokeWidth={2.5} />
        </span>
      )}
      <span>{children}</span>
      {typeof count === 'number' && (
        <span className={styles.count} aria-hidden="true">{count}</span>
      )}
    </button>
  );
};

Chip.propTypes = {
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  count: PropTypes.number,
};
Chip.defaultProps = {
  active: false,
  disabled: false,
  onClick: () => {},
  children: null,
  count: undefined,
};

const FilterChips = ({
  items,
  value,
  onChange,
  multi,
  onClear,
  className,
  ariaLabel,
}) => {
  const valueArr = Array.isArray(value) ? value : value ? [value] : [];

  const handleClick = (key, disabled) => {
    if (disabled) return;
    if (multi) {
      const next = valueArr.includes(key)
        ? valueArr.filter((k) => k !== key)
        : [...valueArr, key];
      onChange(next);
    } else {
      onChange(key === valueArr[0] ? null : key);
    }
  };

  const cls = [styles.row, className || ''].filter(Boolean).join(' ');

  return (
    <div className={cls} role="group" aria-label={ariaLabel}>
      {items.map((it) => (
        <Chip
          key={it.key}
          active={valueArr.includes(it.key)}
          disabled={it.disabled}
          count={it.count}
          onClick={() => handleClick(it.key, it.disabled)}
        >
          {it.label}
        </Chip>
      ))}
      {onClear && valueArr.length > 0 && (
        <button
          type="button"
          className={styles.clear}
          onClick={onClear}
          aria-label="Clear filters"
        >
          <X size={14} strokeWidth={1.75} />
          Clear
        </button>
      )}
    </div>
  );
};

FilterChips.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      count: PropTypes.number,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onChange: PropTypes.func.isRequired,
  multi: PropTypes.bool,
  onClear: PropTypes.func,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

FilterChips.defaultProps = {
  value: null,
  multi: false,
  onClear: null,
  className: '',
  ariaLabel: 'Filters',
};

export default FilterChips;
